import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { Page } from '@/shared/ui/Page';
import { PageHeader } from '@/shared/ui/PageHeader';
import { GreenLiquidGlassButton } from '@/shared/ui/GreenLiquidGlassButton';
import { subscriptionApi } from '@/entities/subscription/api';
import { notificationApi } from '@/entities/notification/api';
import { cn } from '@/shared/lib/cn';
import { useT } from '@/shared/lib/i18n';

const NOTIFICATION_EVENTS = [
  'charge_created', 'reminder', 'price_changed',
  'payment_marked', 'payment_confirmed', 'payment_rejected',
  'receipt_requested', 'receipt_uploaded',
  'exit_request_received', 'exit_approved', 'exit_declined',
  'manual_correction_created', 'operation_reversed',
  'subscription_closed', 'subscription_paused', 'subscription_resumed',
  'invite_accepted', 'member_force_removed', 'participant_unreachable',
] as const;

export function SubscriptionSettingsPage() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const t = useT();
  const [confirmationRequired, setConfirmationRequired] = useState(false);
  const [offsetsText, setOffsetsText] = useState('-3,0,3');
  const [offsetsTouched, setOffsetsTouched] = useState(false);

  const detailQ = useQuery({
    queryKey: ['subscription', id],
    queryFn: () => subscriptionApi.getDetail(id!),
    enabled: !!id,
  });

  useEffect(() => {
    if (detailQ.data?.subscription.payment_confirmation_required !== undefined) {
      setConfirmationRequired(Boolean(detailQ.data.subscription.payment_confirmation_required));
    }
  }, [detailQ.data]);

  // The GET response never includes the subscription's real reminder offsets
  // (backend gap), so we cannot pre-fill this field with the actual saved
  // value. To avoid silently overwriting it with a fabricated default, we
  // only send `reminder_offsets` in the payload once the user has actually
  // edited this field.
  const trimmedSegments = offsetsText
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  const isOffsetsEmpty = trimmedSegments.length === 0;
  const parsedOffsets = trimmedSegments.map((s) => Number(s));
  const hasInvalidOffsets = !isOffsetsEmpty && parsedOffsets.some((n) => !Number.isInteger(n));
  // An emptied-out field is treated as "not touched" (equivalent to the
  // untouched case): it simply omits reminder_offsets from the payload
  // rather than submitting `[]` or showing an error.
  const shouldSendOffsets = offsetsTouched && !isOffsetsEmpty;
  const canSubmit = isOffsetsEmpty || !hasInvalidOffsets;

  const mutation = useMutation({
    mutationFn: () =>
      subscriptionApi.updateSettings(id!, {
        payment_confirmation_required: confirmationRequired,
        ...(shouldSendOffsets ? { reminder_offsets: parsedOffsets } : {}),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['subscription', id] });
    },
  });

  const notifSettingsQ = useQuery({
    queryKey: ['notification-settings', id],
    queryFn: () => notificationApi.list(id),
    enabled: !!id,
  });

  const notifMutation = useMutation({
    mutationFn: ({ event, enabled }: { event: string; enabled: boolean }) =>
      notificationApi.set(event, enabled, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notification-settings', id] }),
  });

  const notifSettings = notifSettingsQ.data?.settings ?? {};

  return (
    <Page>
      <PageHeader title={<span className="font-bold text-[19px]">{t('subscriptionSettings.title')}</span>} />
      <div className="px-[18px] pb-5 flex flex-col gap-4 min-h-0">
        <button
          className={cn(
            'flex items-center justify-between rounded-[12px] px-4 py-3 text-[14px] bg-white/50 border',
            confirmationRequired ? 'border-2 border-brand-green bg-brand-green/10' : 'border-white/60',
          )}
          onClick={() => setConfirmationRequired((v) => !v)}
        >
          <span className="text-left">
            <span className="block">{t('subscriptionSettings.confirmationMode')}</span>
            <span className="block text-[12px] text-text-muted">{t('subscriptionSettings.confirmationModeHint')}</span>
          </span>
          <span className={cn('w-[18px] h-[18px] rounded-full border-2 shrink-0', confirmationRequired ? 'border-brand-green bg-brand-green' : 'border-[#b9c4bb]')} />
        </button>

        <label className="flex flex-col gap-2">
          <span className="text-[14px] font-semibold text-text-secondary">{t('subscriptionSettings.reminderOffsets')}</span>
          <input
            value={offsetsText}
            onChange={(event) => {
              setOffsetsText(event.target.value);
              setOffsetsTouched(true);
            }}
            className="rounded-[12px] px-4 py-3 text-[15px] bg-white/55 border border-white/60"
            placeholder="-3,0,3"
          />
        </label>

        <div className="flex flex-col gap-2">
          <p className="text-[14px] font-semibold text-text-secondary">{t('subscriptionSettings.notificationsTitle')}</p>
          {NOTIFICATION_EVENTS.map((event) => {
            const enabled = notifSettings[event] ?? true;
            return (
              <button
                key={event}
                className="flex items-center gap-3 rounded-[12px] px-4 py-3 bg-white/50 border border-white/60 disabled:opacity-60"
                disabled={notifMutation.isPending}
                onClick={() => notifMutation.mutate({ event, enabled: !enabled })}
              >
                <span className="flex-1 text-left text-[14px] font-medium">{t(`settings.events.${event}`)}</span>
                <span className={cn(
                  'w-[46px] h-[28px] rounded-full p-[3px] transition-colors',
                  enabled ? 'bg-brand-green' : 'bg-black/10',
                )}>
                  <span className={cn(
                    'block w-[22px] h-[22px] rounded-full bg-white shadow-sm transition-transform',
                    enabled && 'translate-x-[18px]',
                  )} />
                </span>
              </button>
            );
          })}
        </div>

        <div className="mt-auto">
          {hasInvalidOffsets && (
            <p className="text-[13px] text-status-debt text-center mb-2">{t('subscriptionSettings.invalidOffsets')}</p>
          )}
          {mutation.isError && (
            <p className="text-[13px] text-status-debt text-center mb-2">{t('subscriptionSettings.error')}</p>
          )}
          {mutation.isSuccess && (
            <p className="text-[13px] text-status-paid text-center mb-2">{t('subscriptionSettings.saved')}</p>
          )}
          <GreenLiquidGlassButton
            loading={mutation.isPending}
            disabled={!canSubmit}
            onClick={() => mutation.mutate()}
          >
            {t('subscriptionSettings.save')}
          </GreenLiquidGlassButton>
        </div>
      </div>
    </Page>
  );
}
